/**
 * WhatsApp Flows hook — CRUD + publish + send for flows stored in the Communications
 * DB (api/whatsapp/flows). `resp.data` is the unwrapped ResponseData payload.
 */

import { useState, useCallback } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';
import { toast } from '../../../../components/ui/providers/AlertProvider';

export interface FlowListItem {
  flowId: number;
  name: string;
  statusId: number;
  category?: string | null;
  metaFlowId?: string | null;
  screenCount: number;
  createdDateTime: number;
  modifiedDateTime: number;
}

export interface FlowView {
  flowId: number;
  name: string;
  description?: string | null;
  statusId: number;
  category?: string | null;
  metaFlowId?: string | null;
  definition?: string | null;
  flowJson?: string | null;
  defaultBodyText?: string | null;
  defaultHeaderText?: string | null;
  defaultFooterText?: string | null;
  defaultCta?: string | null;
  flowAction?: string | null;
  screenCount: number;
  createdDateTime: number;
  modifiedDateTime: number;
}

export interface SaveFlowRequest {
  name: string;
  description?: string | null;
  category?: string | null;
  definition?: string | null;
  flowJson?: string | null;
  defaultBodyText?: string | null;
  defaultHeaderText?: string | null;
  defaultFooterText?: string | null;
  defaultCta?: string | null;
  flowAction?: string | null;
  screenCount?: number;
}

export interface SendFlowRunRequest {
  recipients: string[];
  bodyText?: string;
  cta?: string;
  headerText?: string;
  footerText?: string;
  flowAction?: string;
  flowActionPayload?: unknown;
}

export interface FlowSendResult {
  sent: number;
  failed: number;
  results: { toAddress: string; ok: boolean; error?: string | null }[];
}

const path = (p: string) => getApiUrl('communications', `/api/whatsapp/flows${p}`);

export function useWhatsAppFlows() {
  const httpClient = useHttpClient();
  const [saving, setSaving] = useState(false);

  const listFlows = useCallback(async (): Promise<FlowListItem[]> => {
    const resp = await httpClient.get<FlowListItem[]>(path(''));
    return (resp.data as FlowListItem[]) ?? [];
  }, [httpClient]);

  const getFlow = useCallback(async (id: number): Promise<FlowView | null> => {
    const resp = await httpClient.get<FlowView>(path(`/${id}`));
    return (resp.data as FlowView) ?? null;
  }, [httpClient]);

  const createFlow = useCallback(async (body: SaveFlowRequest): Promise<FlowView | null> => {
    setSaving(true);
    try {
      const resp = await httpClient.post<FlowView>(path(''), body);
      toast.success('Flow created');
      return (resp.data as FlowView) ?? null;
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create flow');
      return null;
    } finally {
      setSaving(false);
    }
  }, [httpClient]);

  const updateFlow = useCallback(async (id: number, body: Partial<SaveFlowRequest> & { statusId?: number }): Promise<FlowView | null> => {
    setSaving(true);
    try {
      const resp = await httpClient.put<FlowView>(path(`/${id}`), body);
      toast.success('Flow saved');
      return (resp.data as FlowView) ?? null;
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save flow');
      return null;
    } finally {
      setSaving(false);
    }
  }, [httpClient]);

  const deleteFlow = useCallback(async (id: number): Promise<boolean> => {
    try {
      await httpClient.delete(path(`/${id}`));
      toast.success('Flow deleted');
      return true;
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete flow');
      return false;
    }
  }, [httpClient]);

  const publishFlow = useCallback(async (id: number, metaFlowId: string): Promise<FlowView | null> => {
    setSaving(true);
    try {
      const resp = await httpClient.post<FlowView>(path(`/${id}/publish`), { metaFlowId });
      toast.success(metaFlowId.trim() ? 'Flow marked as published' : 'Flow ID cleared');
      return (resp.data as FlowView) ?? null;
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update flow');
      return null;
    } finally {
      setSaving(false);
    }
  }, [httpClient]);

  const sendFlow = useCallback(async (id: number, body: SendFlowRunRequest): Promise<FlowSendResult | null> => {
    setSaving(true);
    try {
      const resp = await httpClient.post<FlowSendResult>(path(`/${id}/send`), body);
      const r = resp.data as FlowSendResult;
      toast.success(`${r?.sent ?? 0} sent, ${r?.failed ?? 0} failed`);
      return r ?? null;
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send flow');
      return null;
    } finally {
      setSaving(false);
    }
  }, [httpClient]);

  return { saving, listFlows, getFlow, createFlow, updateFlow, deleteFlow, publishFlow, sendFlow };
}
